import {
  CustomerDetail,
  CustomerDuplicates,
  CustomerHistory,
  CustomerList,
  Quote,
  QuoteList,
  QuoteReply,
  type QuoteStatus,
} from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useDebounced } from './use-debounced';

export const quotesKey = ['quotes'] as const;
export const customersKey = ['customers'] as const;

export interface QuoteFilters {
  status?: QuoteStatus | null;
  channelId?: string | null;
  customerId?: string | null;
  q?: string;
}

export function quoteParams(filters: QuoteFilters): URLSearchParams {
  const params = new URLSearchParams({ limit: '100' });
  if (filters.status) params.set('status', filters.status);
  if (filters.channelId) params.set('channelId', filters.channelId);
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  return params;
}

export function useQuotes(filters: QuoteFilters) {
  const params = quoteParams(filters);
  return useQuery({
    queryKey: [...quotesKey, 'list', params.toString()],
    queryFn: () => api.get(`/quotes?${params.toString()}`, QuoteList),
    placeholderData: (prev) => prev,
  });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: [...quotesKey, id],
    queryFn: () => api.get(`/quotes/${id}`, Quote),
    enabled: !!id,
  });
}

export function useQuoteReply(id: string | undefined, templateId: string | null) {
  return useQuery({
    queryKey: [...quotesKey, id, 'reply', templateId],
    queryFn: () =>
      api.get(`/quotes/${id}/reply${templateId ? `?templateId=${templateId}` : ''}`, QuoteReply),
    enabled: !!id,
  });
}

export function useCustomers(q: string, enabled = true) {
  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  return useQuery({
    queryKey: [...customersKey, 'list', params.toString()],
    queryFn: () => api.get(`/customers?${params.toString()}`, CustomerList),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useCustomer(id: string | null | undefined) {
  return useQuery({
    queryKey: [...customersKey, id],
    queryFn: () => api.get(`/customers/${id}`, CustomerDetail),
    enabled: !!id,
  });
}

/** Customers like the one being typed (same first name or WhatsApp), for "Já existe…". */
export function useCustomerDuplicates(name: string, whatsapp: string) {
  const term = useDebounced(`${name.trim()}|${whatsapp.replace(/\D/g, '')}`, 300);
  const [n = '', w = ''] = term.split('|');
  const params = new URLSearchParams({ name: n, whatsapp: w });
  return useQuery({
    queryKey: [...customersKey, 'duplicates', term],
    queryFn: () => api.get(`/customers/duplicates?${params.toString()}`, CustomerDuplicates),
    enabled: n.length >= 3 || w.length >= 8,
  });
}

export function useCustomerHistory(id: string) {
  return useQuery({
    queryKey: [...customersKey, id, 'history'],
    queryFn: () => api.get(`/customers/${id}/history`, CustomerHistory),
  });
}

/** After any quote or customer change: lists, details and the home counters refresh. */
export function useInvalidateQuotes() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: quotesKey });
    void qc.invalidateQueries({ queryKey: customersKey });
  };
}
